import { useTranslation } from 'react-i18next';
import { DetailRow, Skeleton } from 'vopay';
import { SwitchIndicator } from 'UI';
import { renderDetailValue } from '@/components/Entities/UI/DetailValue/detailValue';

/**
 * A read-only entitlement row for `DetailRows` (Figma 171:38570 specifies the Switch at
 * `State="Disabled"`). Marked disabled so the cursor says it cannot be toggled — editing is a spec
 * non-goal — while the library keeps an enabled entitlement visibly distinct from a switched-off one.
 * The state is also spelled out for assistive technology and for anything asserting on the row.
 * `checked` is undefined while the account loads and null when the request failed.
 */
export default function useSwitchDetailRow() {
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  return (label: string, checked: boolean | null | undefined, testId: string): DetailRow => ({
    label,
    'data-testid': testId,
    value:
      checked === undefined ? (
        <Skeleton width={46} height={28} />
      ) : // A failed entitlement reads as the same dash every other failed row does; a blank cell would
      // read as "off".
      checked === null ? (
        renderDetailValue(null)
      ) : (
        <span role="img" aria-label={checked ? tGlobal('yes') : tGlobal('no')} data-checked={checked}>
          <SwitchIndicator checked={checked} disabled />
        </span>
      ),
  });
}
